import React from 'react';

const WHAT_WE_BUILD = [
  { title: '💬 Prompt-Driven Pipelines', desc: 'Build and manage data pipelines using simple, natural language prompts.' },
  { title: '🐍 AI-Generated Python', desc: 'Generate production-ready Python workflows and runners paired with target SQL.' },
  { title: '🔄 End-to-End ETL', desc: 'Handle ETL processes from ingestion to transformation, validation, and deployment.' },
  { title: '🗂️ Intelligent Schema Mapping', desc: 'Introspect live system catalogs to map columns and types without guesswork.' },
  { title: '🤖 Autonomous Agent Platform', desc: 'Execute multi-step data tasks (/agent) with background runtime, approvals, and persistent memory.' },
  { title: '📊 Conversational NL→SQL', desc: 'Ask plain English questions against connected databases with live tables and interactive charts.' },
  { title: '🛡️ Self-Healing ETL & Drift', desc: 'Detect upstream schema drift and generate automated repair patches before batch jobs fail.' },
  { title: '🔑 BYOK & Local AI Privacy', desc: 'Bring your own keys across 12+ LLMs or run air-gapped on-device models with Ollama.' },
  { title: '☁️ Cloud & Hybrid Deploy', desc: 'Deploy scalable data workflows across cloud, hybrid, and air-gapped enterprise environments.' },
];

const PHILOSOPHY = [
  { title: 'Developer-First Experience', desc: 'Every feature is designed to feel intuitive, fast, and native to how developers think and work.' },
  { title: 'Simplicity Through Intelligence', desc: 'We replace complexity with AI-assisted workflows that adapt to context instead of requiring configuration-heavy setups.' },
  { title: 'Transparency & Control', desc: 'Even with AI-driven development, developers remain in full control of logic, structure, and execution.' },
  { title: 'Vibe Coding for Data Engineering', desc: 'We believe the future of building data systems lies in expressive, prompt-driven development where ideas become pipelines instantly.' },
];

export default function AboutPage() {
  return (
    <div className="animate-fade-in bg-slate-50">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 to-indigo-50/40 py-20 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-none mb-4">
                About <span className="text-blue-600">Candor</span>
              </h1>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 leading-tight mb-6">
                Built for Data, <span className="text-blue-600">Driven by Vibe Coding</span>
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Candor is a next-generation data platform built specifically to eliminate manual, repetitive syntax alignment from modern data engineering. While generalist AI assistants excel at writing isolated code snippets, they frequently struggle when dropped into complex, real-world database environments because they are blind to the underlying infrastructure.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                Candor was engineered to bridge this gap. By embedding deep schema awareness natively into an agentic environment, it completely eliminates the costly "fail-and-fix" debugging loops that plague everyday development.
              </p>
              <div className="flex gap-4 flex-wrap">
                <a
                  href="#mission"
                  className="px-6 py-3.5 rounded-xl text-base font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-xl shadow-blue-500/15 hover:shadow-blue-500/30 transition-all hover:-translate-y-0.5"
                >
                  Our Mission
                </a>
                <a
                  href="#what-we-build"
                  className="px-6 py-3.5 rounded-xl text-base font-semibold text-blue-600 hover:text-blue-700 bg-white border border-blue-200 shadow-sm transition-all"
                >
                  What We Build
                </a>
              </div>
            </div>
            
            <div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-slate-950">
                <img
                  src="/assets/about_hero_visual_1778832381990.png"
                  alt="AI and data architecture platform illustration"
                  className="w-full h-auto block object-cover brightness-90"
                  onError={(e) => {
                    e.target.src = '/assets/pexels-photo-8386440.jpeg';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section id="mission" className="py-20 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-50 border border-slate-200/60 rounded-3xl p-8 sm:p-10 shadow-sm text-center">
            <div className="text-4xl mb-4">🎯</div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Our Mission</h2>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              Whether you are automating enterprise ETL pipelines, using reusable prompt skills to standardize your workflows, or rapidly scaffolding a new mini-project, Candor delivers a zero-friction workspace where the AI understands your data model perfectly on the very first try.
            </p>
          </div>
        </div>
      </section>

      {/* What We Build */}
      <section id="what-we-build" className="py-20 bg-slate-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight sm:text-4xl">What We Build</h2>
            <p className="mt-4 text-slate-600">
              Candor is a next-generation data platform that helps developers and data teams work smarter with AI-assisted pipeline development.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHAT_WE_BUILD.map((item, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200/50">
                <h3 className="text-md font-bold text-blue-600 mb-3">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <p className="text-center mt-12 max-w-3xl mx-auto text-slate-500 text-sm leading-relaxed">
            At its core, Candor brings vibe coding to data engineering—where complex pipeline development becomes a guided, intelligent, and collaborative experience between developers and AI.
          </p>
        </div>
      </section>

      {/* Why Candor Exists */}
      <section id="why-candor" className="py-20 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 tracking-tight sm:text-4xl mb-6">Why Candor Exists</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Modern data ecosystems are powerful—but overly complex. Teams often spend more time wiring systems together than actually deriving value from data.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                Candor was created to remove that friction.
              </p>
              <p className="text-slate-600 leading-relaxed">
                We aim to eliminate repetitive engineering effort, reduce dependency on manual pipeline coding, and help developers focus on logic, insights, and outcomes rather than infrastructure overhead.
              </p>
            </div>

            <div>
              <div className="bg-slate-900 text-white rounded-3xl p-8 sm:p-10 shadow-2xl">
                <h3 className="text-xl font-bold mb-6 text-slate-100">The Problem We Solve</h3>
                <ul className="space-y-4 text-slate-300 text-sm sm:text-base leading-relaxed">
                  <li className="flex items-start gap-3">
                    <span className="text-blue-400 font-bold">🔹</span>
                    <span>Too much time spent on wiring, not building</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-400 font-bold">🔹</span>
                    <span>Repetitive manual pipeline coding</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-400 font-bold">🔹</span>
                    <span>Infrastructure overhead slowing delivery</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-400 font-bold">🔹</span>
                    <span>Less focus on insights and business outcomes</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Philosophy */}
      <section id="philosophy" className="py-20 bg-slate-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight sm:text-4xl">Our Philosophy</h2>
            <p className="mt-4 text-slate-600">We build with a few core principles.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {PHILOSOPHY.map((item, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-8 border border-slate-200/50 shadow-sm hover:shadow-md hover:border-blue-500/20 transition-all">
                <h3 className="text-lg font-bold text-slate-800 mb-3">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Platform */}
      <section id="platform" className="py-20 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <h2 className="text-3xl font-bold text-slate-900 tracking-tight sm:text-4xl mb-6">The Platform</h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Candor is more than a tool—it is a complete data engineering environment powered by an Agentic AI IDE.
              </p>
              <p className="font-bold text-slate-800 mb-4">It enables teams to:</p>
              <ul className="space-y-3 pl-4 text-slate-600 leading-relaxed">
                <li className="list-disc">Design workflows using natural language</li>
                <li className="list-disc">Generate and execute Python-based data pipelines</li>
                <li className="list-disc">Orchestrate end-to-end ETL processes</li>
                <li className="list-disc">Maintain observability, validation, and reliability by design</li>
              </ul>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-slate-200 overflow-hidden shadow-2xl bg-slate-950">
                <img
                  src="/assets/vibe_coding_hero.png"
                  alt="Candor Agentic AI IDE workspace for data engineering"
                  className="w-full h-auto block"
                  onError={(e) => {
                    e.target.src = '/assets/pexels-photo-8386440.jpeg';
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Built for the Future & Candor Flagship */}
      <section id="offerings" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 tracking-tight sm:text-4xl mb-6">Built for the Future of Data</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Data engineering is evolving rapidly. Candor is built for that evolution—where AI becomes a core collaborator in building, managing, and scaling data systems.
              </p>
              <p className="text-slate-600 leading-relaxed">
                We are building the future where data pipelines are not written line by line, but shaped through intent.
              </p>
            </div>

            <div>
              <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-lg hover:shadow-xl transition-shadow h-full flex flex-col justify-center">
                <div className="text-4xl mb-4">🚀</div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Candor</h2>
                <p className="text-slate-600 leading-relaxed">
                  Candor is our flagship product, bringing together AI-driven pipeline creation, cognitive analytics, and developer-focused tooling into a single unified experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
